import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {

  Menu,
  X,
  Shield,
  LogOut,
  User,
  ChevronDown,
  ArrowLeft,
  UserCircle
} from 'lucide-react';

const TopNavBar = ({ toggleSidebar, sidebarCollapsed, mobileMenuOpen, setMobileMenuOpen }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const location = useLocation();

  const getPageHeaderInfo = (pathname) => {
    if (pathname === '/') return {
      title: 'Dashboard Overview',
      subtitle: "Welcome back! Here's what's happening today."
    };
    if (pathname.includes('/sales/products')) return {
      title: 'Products Management',
      subtitle: 'Manage your product catalog and inventory.'
    };
    if (pathname.includes('/sales/customers')) return {
      title: 'Customer Management',
      subtitle: 'View and manage your customer database.'
    };
    if (pathname.includes('/sales/estimates')) return {
      title: 'Estimates',
      subtitle: 'Create and track sales estimates.'
    };
    if (pathname.includes('/sales/closed-orders')) return {
      title: 'Closed Orders',
      subtitle: 'View and manage confirmed orders.'
    };
    if (pathname.includes('/sales/invoices')) return {
      title: 'Invoices',
      subtitle: 'Track payments and manage invoices.'
    };
    if (pathname.includes('/profile')) return {
      title: 'User Profile',
      subtitle: 'Manage your account settings.'
    };
    if (pathname.includes('/gallery')) return {
      title: 'Product Gallery',
      subtitle: 'Browse products and manage catalog media.'
    };
    if (pathname.includes('/admin')) return {
      title: 'Admin Portal',
      subtitle: 'Administrative controls and reports.'
    };

    return {
      title: 'Dashboard Overview',
      subtitle: "Welcome back! Here's what's happening today."
    };
  };

  const { title, subtitle } = getPageHeaderInfo(location.pathname);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const handleGoToAdminPortal = () => {
    navigate('/admin/dashboard');
    setShowUserMenu(false);
  };

  return (
    <header
      className={`fixed h-16 bg-white border-b border-slate-200 z-30 transition-all duration-300 ${sidebarCollapsed ? 'left-0 lg:left-20' : 'left-0 lg:left-64'
        } right-0`}
      style={{
        top: user?.isSandbox ? 'var(--sandbox-banner-height)' : '0'
      }}
    >
      <div className="flex items-center justify-between h-full px-4 md:px-6">
        {/* Left Section */}
        <div className="flex items-center space-x-3 md:space-x-4">
          {/* Hamburger Menu - Mobile Only */}
          <button
            onClick={() => {
              console.log('TopNavBar hamburger clicked, current state:', mobileMenuOpen);
              if (setMobileMenuOpen) {
                setMobileMenuOpen(!mobileMenuOpen);
              }
            }}
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer z-50 relative"
            aria-label="Toggle menu"
            type="button"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-slate-600 pointer-events-none" />
            ) : (
              <Menu className="h-6 w-6 text-slate-600 pointer-events-none" />
            )}
          </button>
          <div className="hidden md:block">
            <h1 className="text-lg md:text-xl font-semibold text-slate-900">{title}</h1>
            <p className="text-xs md:text-sm text-slate-500">{subtitle}</p>
          </div>
          <div className="md:hidden">
            <h1 className="text-base font-semibold text-slate-900">IDEALTRENDZ</h1>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center space-x-4">


          {/* User Menu or Admin Login */}
          {user ? (
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <User className="h-5 w-5 text-slate-600" />
                <span className="text-sm font-medium text-slate-700 hidden sm:inline">{user?.name || 'User'}</span>
                <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                  <button
                    onClick={() => {
                      navigate('/profile');
                      setShowUserMenu(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-white hover:text-slate-900 transition-colors border-b border-slate-100"
                  >
                    <UserCircle className="h-4 w-4 mr-3 text-gray-500" />
                    My Profile
                  </button>
                  {user?.userType === 'admin' && (
                    <button
                      onClick={handleGoToAdminPortal}
                      className="flex items-center w-full px-4 py-2 text-sm text-[#0A5F38] hover:bg-blue-50 transition-colors border-b border-slate-100"
                    >
                      <ArrowLeft className="h-4 w-4 mr-3 text-blue-500" />
                      Navigate Back to Admin Portal
                    </button>
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="h-4 w-4 mr-3 text-red-500" />
                    Log Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => navigate('/login')}
              className="flex items-center space-x-2 px-4 py-2 bg-[#C5A57D] text-white rounded-lg hover:bg-[#B8985E] transition-colors"
            >
              <Shield className="h-4 w-4" />
              <span className="text-sm font-medium">Login</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopNavBar;



